<template>
  <div class="acc-flight">
    <div class="acc-flight__top">
      <div class="acc-flight__left">
        <div class="acc-flight__id">
          {{ orderNumber }}.
          <router-link
            v-if="!isNameLinkDisable"
            :to="{ name: 'reservation-flights-general', params: { id: reservation.id } }"
            class="acc-flight__link"
          >
            Reservation #ATF - {{ reservation.id }}
          </router-link>
          <span v-else>Reservation #ATF - {{ reservation.id }}</span>
        </div>
        <div class="acc-flight__tower">
          <div class="acc-flight__name-wrapper">
            <div class="acc-flight__name">
              {{ reservation.origin.name }}
              <img src="@/assets/img/arrow-right-gray-icon.svg" alt="">
              {{ reservation.destination.name }}
            </div>
            <div v-if="reservation.amendment_request" :style="{ 'backgroundColor': '#f0c427'  }"
                 class="acc-flight__status">
              Amendment requested
            </div>
            <div v-if="reservation.cancellation_request" :style="{ 'backgroundColor': '#f36161'  }"
                 class="acc-flight__status">
              Cancellation requested
            </div>
            <div
              v-if="!reservation.seats_assignment && reservation.reservation_status.code === 'booked'"
              :style="{ 'backgroundColor': '#439ad1' }"
              class="acc-flight__status"
            >
              Missing seats assignment
            </div>
            <div
              v-if="countdown"
              :style="{ 'backgroundColor': calculateTimeDiff(countdown).difference < 360 ? '#77b017' : calculateTimeDiff(countdown).difference < 1440 ? '#f0c427' : '#e85c20' }"
              class="acc-flight__status"
            >
              {{ calculateTimeDiff(countdown).time_string }}
            </div>
          </div>
          <div class="acc-flight__timing">
            <div class="acc-flight__time">
              <div class="acc-flight__time-text">Date:</div>
              <div class="acc-flight__time-value">{{ dateFormat(reservation.date) }}</div>
            </div>
            <div class="acc-flight__time">
              <div class="acc-flight__time-text">Departure:</div>
              <div class="acc-flight__time-value">{{ reservation.departure }}</div>
            </div>
            <div class="acc-flight__time">
              <div class="acc-flight__time-text">Arrival:</div>
              <div class="acc-flight__time-value">{{ reservation.arrival }}</div>
            </div>
            <div class="acc-flight__time">
              <div class="acc-flight__time-text">Airline:</div>
              <div class="acc-flight__time-value">{{ reservation.airline.name }}</div>
            </div>
            <div
              v-if="reservation.reservation_status.code === 'booked_provisionally' && reservation.reservation_deadline"
              class="acc-flight__time"
            >
              <div class="acc-flight__time-text">Deadline:</div>
              <div class="acc-flight__time-value">{{ dateFormat(reservation.reservation_deadline) }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <button class="acc-flight__button" type="button" @click="is_acc_open = !is_acc_open">
      <span class="acc-flight__status-block">
        <span
          v-if="reservation.lead && reservation.lead.reservation_manager"
          v-tooltip="{ content: reservation.lead.reservation_manager.full_name, placement: 'right', offset: 10 }"
          class="acc-flight__photo"
        >
          <img :src="getUserAvatar(reservation.lead.reservation_manager.crop.crop_url)" alt="Avatar">
        </span>
        <span v-else class="acc-flight__photo">
          <img alt="Avatar" src="@/assets/img/temp/avatar-slug.jpg">
        </span>
        <span :style="{ 'backgroundColor': reservation.reservation_status.color || '#000'  }"
              class="acc-flight__status">
          {{ reservation.reservation_status.name }}
        </span>
        <img v-if="reservation.notes" alt="" src="@/assets/img/transfer-notes-icon.svg">
      </span>
      <span
        v-tooltip="{ content: reservation.lead ? reservation.lead.manager.full_name : '', placement: 'top-start', offset: 10 }"
        class="acc-flight__photo"
      >
        <img :src="getUserAvatar(reservation.lead ? reservation.lead.manager.crop.crop_url : '')" alt="Avatar">
      </span>
      <span :style="{ 'backgroundColor': reservation.sales_status.color || '#000'  }" class="acc-flight__status">
        {{ reservation.sales_status.name }}
      </span>
      <span class="acc-flight__time">
        <span class="acc-flight__time-text">Client:</span>
        <span class="acc-flight__name">
        {{ reservation.lead.name ? reservation.lead.name : `Lead #${reservation.lead?.id}` }}
      </span>
      </span>
      <span :class="{ 'acc-flight__toggler--open': is_acc_open }" class="acc-flight__toggler">
        <img alt="" src="@/assets/img/arrow-toggler-bottom-icon.svg">
      </span>
    </button>
    <transition name="slide-block">
      <div v-if="is_acc_open" class="acc-flight__dropdown">
        <div class="acc-flight__list">
          <app-lug-item
            v-for="(lugage, lugage_index) in reservation.seats_short"
            :key="`lug_${lugage_index}`"
            :orderNumber="lugage_index + 1"
            :ageType="lugage.luggage_option.age_type"
            :weight="lugage.luggage_option.weight"
            :price="lugage.luggage_option.price"
            :clientSelected="lugage.client"
            :count="lugage.count"
            :seats-assignment="true"
            :deleted="false"
          />
        </div>
        <div v-if="reservation.notes" class="acc-flight__notes">
          <div class="acc-flight__note-icon">
            <img alt="" src="@/assets/img/note-green-big-icon.svg">
            <span>NOTE (by manager):</span>
          </div>
          <div class="acc-flight__text">{{ reservation.notes }}</div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { differenceInMinutes, format, parse } from 'date-fns'
import AppLugItem from '@/components/AppLugItem.vue'

export default {
  name: 'AppAccordionFlight',
  components: { AppLugItem },
  props: {
    reservation: {
      type: Object,
      required: true,
    },
    orderNumber: {
      type: [Number, String],
      default: 0
    },
    countdown: {
      type: String,
      default: ''
    },
    isNameLinkDisable: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      is_acc_open: false
    }
  },
  methods: {
    calculateTimeDiff(start) {
      const difference = differenceInMinutes(new Date(), new Date(start))
      const minutes = difference % 60
      const hours = Math.trunc((difference / 60) % 24)
      const days = Math.trunc(difference / 60 / 24)

      return {
        time_string: `${days ? `${days}d ` : ''}${hours ? `${hours}h ` : ''}${minutes}m`,
        difference
      }
    },
    dateFormat(date) {
      return format(parse(date, 'yyyy-MM-dd', new Date()), 'dd MMM yyyy')
    },
    getUserAvatar(crop_url) {
      if (crop_url) return crop_url
      return require('@/assets/img/temp/avatar-slug.jpg')
    }
  }
}
</script>

<style lang="sass" scoped>
.acc-flight
  margin-bottom: 10px
  border: 1px solid #dfdfdf
  border-radius: 10px
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08)
  background-color: #fff
  overflow: hidden

.acc-flight__top
  display: flex
  align-items: center
  justify-content: space-between
  padding: 7px 16px
  border-bottom: 1px solid #e8e8e8

.acc-flight__left
  display: grid
  grid-template-columns: 240px max-content max-content
  align-items: center
  gap: 20px

.acc-flight__id
  font-weight: 500

.acc-flight__photo
  width: 31px
  line-height: 0
  border-radius: 50%
  overflow: hidden

.acc-flight__link
  display: inline-block
  color: $color-text-base
  font-weight: 500
  transition: color 0.3s

  &:after
    content: ''
    display: block
    height: 2px
    border-radius: 3px
    background-color: #aae182
    transform: translateY(-1px)
    transition: opacity 0.3s

  &:hover
    &:after
      opacity: 0

.acc-flight__name-wrapper
  display: flex
  align-items: center
  gap: 10px
  margin-bottom: 6px

.acc-flight__name
  font-weight: 500

.acc-flight__nights
  color: #5f5f5f

.acc-flight__button
  position: relative
  display: grid
  grid-template-columns: 240px 31px max-content max-content max-content
  align-items: center
  gap: 20px
  width: 100%
  padding: 10px 16px
  cursor: pointer
  text-align: left

.acc-flight__status-block
  display: inline-flex
  align-items: center
  gap: 12px

.acc-flight__status
  display: inline-block
  min-width: 110px
  padding: 4px 8px
  border-radius: 20px
  color: #fff
  font-size: 13px
  font-weight: 600
  text-align: center

.acc-flight__timing
  display: flex
  align-items: center
  gap: 20px

.acc-flight__time
  display: flex
  gap: 6px

.acc-flight__time-text
  color: #5f5f5f

.acc-flight__time-value
  color: #181818
  font-weight: 500

.acc-flight__toggler
  position: absolute
  top: 14px
  right: 18px
  transition: transform 0.3s

.acc-flight__toggler--open
  transform: rotate(180deg)

.acc-flight__dropdown
  border-top: 1px solid #e8e8e8
  background-color: #fafafa

.acc-flight__list
  padding: 14px

.acc-flight__room
  margin-bottom: 10px

  &:last-child
    margin-bottom: 0

.acc-flight__notes
  display: flex
  align-items: center
  gap: 8px
  padding: 12px 18px 10px
  border-top: 1px solid #e8e8e8

.acc-flight__note-icon
  flex-shrink: 0
  display: flex
  align-items: center
  gap: 10px
  color: #181818
  font-weight: 500

.acc-flight__text
  color: #5f5f5f

.acc-flight__right
  display: flex
  align-items: center
  gap: 10px

// ------------------------ slide-block
.slide-block-enter-active
  transition-duration: 0.3s
  transition-timing-function: ease-in

.slide-block-leave-active
  transition-duration: 0.3s
  transition-timing-function: cubic-bezier(0, 1, 0.5, 1)

.slide-block-enter-to, .slide-block-leave
  max-height: 500px
  overflow: hidden

.slide-block-enter, .slide-block-leave-to
  overflow: hidden
  max-height: 0
</style>